import { onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue';
import {
  addFullscreenChangeListeners,
  exitNativeFullscreen,
  isNativeFullscreenElement,
  shouldUseNativeFullscreen,
  requestNativeFullscreen
} from '@/utils/fullscreen';

interface UseReaderFullscreenOptions {
  containerRef: Ref<HTMLElement | null>;
  forceLandscape: Ref<boolean>;
  isFullscreen: Ref<boolean>;
  showControlsForCurrentViewport: () => void;
}

/**
 * 部分 TS DOM lib / IDE 语言服务未声明 ScreenOrientation.lock，
 * 运行时 API 存在；用交叉类型做兼容访问。
 */
type ScreenOrientationWithLock = ScreenOrientation & {
  lock?: (orientation: 'landscape') => Promise<void>;
};

/** 读取带 lock 能力的屏幕方向对象（步骤 1：兼容类型缺口） */
const getScreenOrientation = (): ScreenOrientationWithLock | undefined =>
  screen.orientation as ScreenOrientationWithLock | undefined;

/**
 * 全屏域：原生全屏与沉浸式全屏（无原生能力时的降级）切换，
 * 沉浸式期间锁定页面滚动，并监听全屏状态变化以保持一致。
 */
export const useReaderFullscreen = (options: UseReaderFullscreenOptions) => {
  const { containerRef, forceLandscape, isFullscreen, showControlsForCurrentViewport } = options;

  const isNativeFullscreen = ref(false);
  const isImmersiveFullscreen = ref(false);
  let removeFullscreenChangeListeners: (() => void) | null = null;
  let restoreImmersiveFullscreenPageLock: (() => void) | null = null;
  let landscapeLockedByReader = false;
  let landscapeLockVersion = 0;

  /** 解锁阅读器触发的横屏锁定（步骤 1：升版本；步骤 2：调用 unlock） */
  const unlockLandscape = () => {
    landscapeLockVersion += 1;
    if (!landscapeLockedByReader) return;
    getScreenOrientation()?.unlock();
    landscapeLockedByReader = false;
  };

  /**
   * 尝试锁定为横屏（需原生全屏 + forceLandscape）。
   * 步骤 1：能力检测；步骤 2：lock；步骤 3：校验仍有效，否则 unlock。
   */
  const tryLockLandscape = async () => {
    const orientation = getScreenOrientation();
    if (!forceLandscape.value || !orientation?.lock) return;
    const version = ++landscapeLockVersion;

    try {
      await orientation.lock('landscape');
      if (
        version !== landscapeLockVersion ||
        !forceLandscape.value ||
        !containerRef.value ||
        !isNativeFullscreenElement(containerRef.value)
      ) {
        orientation.unlock();
        return;
      }
      landscapeLockedByReader = true;
    } catch {
      // CSS 逻辑横屏是兜底；不支持或权限被拒绝时无需打断阅读。
    }
  };

  const syncFullscreenState = () => {
    const container = containerRef.value;
    if (!container) {
      isFullscreen.value = false;
      isNativeFullscreen.value = false;
      isImmersiveFullscreen.value = false;
      unlockLandscape();
      return;
    }

    if (isNativeFullscreenElement(container)) {
      isNativeFullscreen.value = true;
      isImmersiveFullscreen.value = false;
      isFullscreen.value = true;
      return;
    }

    isNativeFullscreen.value = false;
    isFullscreen.value = isImmersiveFullscreen.value;
    unlockLandscape();
  };

  const setImmersiveFullscreenPageLock = (active: boolean) => {
    if (!active) {
      restoreImmersiveFullscreenPageLock?.();
      restoreImmersiveFullscreenPageLock = null;
      return;
    }

    if (restoreImmersiveFullscreenPageLock) return;

    const body = document.body;
    const root = document.documentElement;
    const previousBodyOverflow = body.style.overflow;
    const previousRootOverflow = root.style.overflow;
    const previousBodyOverscrollBehavior = body.style.overscrollBehavior;
    const previousRootOverscrollBehavior = root.style.overscrollBehavior;

    body.style.overflow = 'hidden';
    root.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    root.style.overscrollBehavior = 'none';

    restoreImmersiveFullscreenPageLock = () => {
      body.style.overflow = previousBodyOverflow;
      root.style.overflow = previousRootOverflow;
      body.style.overscrollBehavior = previousBodyOverscrollBehavior;
      root.style.overscrollBehavior = previousRootOverscrollBehavior;
    };
  };

  const toggleFullscreen = async () => {
    const container = containerRef.value;
    if (!container) return;

    if (isNativeFullscreenElement(container)) {
      await exitNativeFullscreen();
    } else if (isImmersiveFullscreen.value) {
      isImmersiveFullscreen.value = false;
    } else {
      try {
        const didRequestNativeFullscreen = shouldUseNativeFullscreen()
          ? await requestNativeFullscreen(container)
          : false;
        isImmersiveFullscreen.value = !didRequestNativeFullscreen;
        if (didRequestNativeFullscreen) await tryLockLandscape();
      } catch (error) {
        console.warn(
          'Failed to enter native fullscreen, using immersive fullscreen fallback.',
          error
        );
        isImmersiveFullscreen.value = true;
      }
    }

    syncFullscreenState();
    showControlsForCurrentViewport();
  };

  watch(isImmersiveFullscreen, setImmersiveFullscreenPageLock);
  watch(forceLandscape, (enabled) => {
    if (!enabled) {
      unlockLandscape();
      return;
    }
    if (isNativeFullscreen.value) void tryLockLandscape();
  });

  onMounted(() => {
    removeFullscreenChangeListeners = addFullscreenChangeListeners(document, syncFullscreenState);
  });

  onBeforeUnmount(() => {
    removeFullscreenChangeListeners?.();
    removeFullscreenChangeListeners = null;
    unlockLandscape();
    setImmersiveFullscreenPageLock(false);
  });

  return {
    isFullscreen,
    isNativeFullscreen,
    isImmersiveFullscreen,
    toggleFullscreen
  };
};
